<template lang="pug">
div
  h1 Privacy Policy
  p
    | PandaFan is committed to protecting and respecting your privacy. This Privacy Policy (this "Policy") sets out how we
    | collect and process personal information about you when you visit our website, when you use our products
    | and services (our "Services"), or when you otherwise do business or make contact with us.
  p
    | Please read this policy carefully to understand how we handle and treat your personal information.
  h2
    | What information do we collect?
  p
    | PandaFan collects data to enable us to operate the Services effectively, and to provide you with the best
    | experiences on our website and our Services. You provide some of this data to us directly, such as when you
    | register to use our Services, subscribe to a newsletter, respond to a survey, make an inquiry through our website,
    | contact us for support, or contact us as a prospective customer, vendor, supplier, or consultant. We get some of
    | your data by recording how you interact with our website and our Services by, for example, using technologies like
    | cookies. We also obtain and process data in the context of providing the Services.
  details
    summary Learn more
    p
      | You have choices about the data we collect. When you are asked to provide personal data, you may decline. But if
      | you choose not to provide data that is necessary to enable us to provide the Services, you may not be able to
      | use all or part of those Services.
    p
      | The data we collect depends on the context of your interactions with PandaFan, the choices you make (including
      | your privacy settings), and the Services you use. The data we collect can include the following:
    ul.list-disc
      li
        p
          strong Name and contact information
          | . We may collect your first and last name, email address,
          |             password, postal address, company information, and other similar contact data.
      li
        p
          strong Payment information
          | . When you make purchases on our website, a secure window will be opened
          |             directly to our payment provider (currently, Stripe), and any payment information that you provide is sent
          |             directly to the payment provider. We have no access to that information.
      li
        p
          strong Device and Usage information
          | . We may collect data about your device and how you and your
          |             device interact with PandaFan and our Services. For example, we may collect:
        ul.list-disc
          li
            p
              em Use data
              | . We may collect data about the features you use and the Services you purchase. This also includes your interactions on our website, and your interactions with us
              |                 via email.
          li
            p
              em Device, connectivity and configuration data
              | . We may collect data about your device and the
              |                 network you use to connect to our Services. This may include IP address, browser type,
              |                 operating system, and referring URLs.
  h2
    | What information do we 
    b NOT 
    | collect?
  p
    | PandaFan collects only the minimum amount of data necessary for the proper functioning of the Service, except for the data you voluntarily provide when using the Service and the data we state will be collected as described above, we promise not to collect browsing history such as target IPs, URLs, etc. that you access through the Service.
  h2
    | What do we use your information for?
  p
    | We use the data we collect to operate our business, and to provide the Services to you. This includes using the
    | data to improve our Services, and to personalize your experiences. We may also use the data to communicate with
    | you to, among other things, inform you about your account, provide security updates, and give you information
    | about the Services. We may also use the data to manage your email subscriptions, improve the relevance and
    | security of our website, respond to user inquiries and send you essential information about our Services.
  details
    summary Learn more
    p
      strong Providing and improving our Services
      | . We use data to provide and improve the Services we offer,
      |         and to perform essential business operations. This includes operating the Services, maintaining and improving
      |         the performance of the Services, developing new features, conducting research, and providing customer support.
      |         Examples of such uses include the following:
    ul.list-disc
      li
        p
          strong Providing the Services
          | . We use data to carry out your transactions with us and to provide
          |             the Services to you. In certain cases, the Services include personalized features and recommendations that
          |             enhance your productivity and enjoyment, and automatically tailor your experience based on the data we have
          |             about you.
      li
        p
          strong Technical support
          | . We use data to diagnose product problems, and to provide other customer
          |             care and support services.
      li
        p
          strong Improving the Services
          | . We use data to continually improve our website and our Services,
          |             including system administration, system security, and adding new features or capabilities.
      li
        p
          strong Business Operations
          | . We use data to develop aggregate analyses and business intelligence
          |             that enable us to operate, protect, make informed decisions, and report on the performance of our business.
      li
        p
          strong Sending Transactional Emails
          | . We may use your data to send you transactional emails and other essential notification emails.
      li
        p
          strong Generally
          | . We use data to respond to your inquiries and requests relating to our Services,
          |             to create and administer your accounts, and to provide us with information and access to resources that you
          |             have requested from us. We also use data for general business purposes, including, among other things, to
          |             improve customer service, to help us improve the content and functionality of our Services, to better
          |             understand our users, to protect against wrongdoing, to enforce our Terms of Service, and to generally
          |             manage our business.
    p
      strong Communications
      | . We use data we collect to communicate with you, and to personalize our
      |         communications with you. For example, we may contact you to inform you when your subscription is ending, to
      |         discuss your account, to let you know when updates are available, to remind you about features of the Services
      |         that are available for your use, or to update you about a support request.
  h2
    | How do we protect your information?
  p
    | We implement a variety of security measures to maintain the safety of your personal information when you enter,
    | submit, or access your personal information. We offer the use of a secure server. All supplied sensitive/credit
    | information is transmitted via Secure Socket Layer (SSL) technology and then encrypted into our Payment gateway
    | providers database only to be accessible by those authorized with special access rights to such systems, and are
    | required to keep the information confidential. After a transaction, your private information (e.g., credit cards,
    | social security numbers, financials) will not be stored on our servers. 
  h2
    | Do we use cookies?
  p
    | Yes. Cookies are small files that a site or its service provider transfers to your computer's hard drive through
    | your Web browser (if you allow) that enable the sites or service provider systems to recognize your browser and
    | capture and remember certain information. You can choose to disable cookies, but if you do, your ability to use or
    | access certain parts of our website may be affected.
  h2
    | Do we disclose any information to outside parties?
  p
    | We share your personal data with your consent, or as necessary to provide the Services to you. We also share your
    | data with vendors working on our behalf; when required by law or to respond to legal processes; to protect our
    | customers; to protect lives; to maintain the security of our Services; and to protect our rights or our property.
  details
    summary Learn more
    p
      | We share your personal data with your consent, or as necessary to provide the Services to you. We also share
      | personal data with vendors or agents working on our behalf for the purposes described in this Policy. For
      | example, companies we have hired to provide cloud hosting services, off-site backups, and customer support may
      | need access to personal data to provide those functions. In such cases, these companies are required to abide by
      | our data privacy and security requirements and are not allowed to use personal data they receive from us for any
      | other purpose.
    p
      | We may disclose your personal data as part of a corporate transaction such as a corporate sale, merger,
      | reorganization, dissolution, or similar event.
    p
      | Finally, we will access, transfer, disclose, and/or preserve personal data, when we have a good faith belief
      | that doing so is necessary to:
    ul.list-disc
      li
        | comply with applicable law or respond to valid legal process, judicial orders, or subpoenas;
      li
        | respond to requests from public or governmental authorities, including for national security or law
        | enforcement purposes;
      li
        | protect the vital interests of our users, customers, or other third parties (including, for example, to
        | prevent spam or attempts to defraud users of our products, or to help prevent the loss of life or serious
        | injury of anyone);
      li
        | operate and maintain the security of our Services, including to prevent or stop an attack on our computer
        | systems or networks;
      li protect the rights, interests or property of PandaFan or third parties;
      li prevent or investigate possible wrongdoing in connection with the Services; or
      li enforce our Terms of Service.
    p We do not sell or trade your personal information to third parties.
  h2
    | Third-Party Links
  p
    | Occasionally, at our discretion, we may include or offer third-party products or services on our website or through our Services. 
    | Suppose you access other websites using the links provided. In that case, the operators of these websites may collect information from you that will be used by them per their privacy policies. 
    | These third-party sites have separate and independent privacy policies. We, therefore, have no responsibility or liability for the content and activities of these linked sites. 
    | Nonetheless, we seek to protect the integrity of our site and welcome any feedback about these sites.
  h2
    | Where we Store and Process Personal Data
  p
    | In order to provide our Services, personal data collected by PandaFan may be stored and processed in the Netherlands and other countries where PandaFan or its affiliates, future subsidiaries, or service providers maintain facilities. 
    | The storage location(s) are chosen to operate efficiently, improve performance, and create redundancies to protect the data in the event of an outage or other problem. 
    | Wherever data is processed, we ensure that the data we collect is processed according to this policy's provisions and the requirements of applicable law.

  h2
    | Data Retention
  p
    | We may retain your personal information as long as you continue to use the Services, have an account with us, or
    | for as long as is necessary to fulfill the purposes outlined in the policy. You can ask to close your account by
    | contacting us at the details above, and we will delete your personal information on request.
  p
    | However, we may retain personal information for an additional period as is permitted or required under applicable
    | laws, for legal, tax, or regulatory reasons, or for legitimate and lawful business purposes.
  details
    summary Learn more
    p
      | We will retain your personal data for as long as necessary to provide the Services to you, or for other
      | essential purposes such as complying with our legal obligations, resolving disputes, and enforcing our
      | agreements. Because these needs can vary for different types of data in the context of the different Services we
      | provide, actual retention periods can vary significantly.
    p  How long is the personal data needed to provide the Services and/or operate our business?
      |  This includes such things such as maintaining and improving the performance of the Services, keeping our systems
      |  secure, and maintaining appropriate business and financial records. This is the general rule that establishes the baseline for most data retention periods.
  h2
    | Changes to our Privacy Policy
  p
    | We will update this privacy statement when necessary to reflect customer feedback and changes in our Services. 
    | We encourage you to review this privacy statement periodically to learn how PandaFan protects your information. 
    | You are responsible for regularly reviewing this Policy. 
    | Continued use of the Services after such changes shall constitute your consent.
  h2
    | How to Contact Us
  p
    | If you have any questions, please contact us via our 
    a(href='/dashboard/support/') support page
    | .
</template>
